import { PHASES, CONSTANTS as C } from "./sim.js"
import { WINDOWS, WINDOW_LABELS, fmtClock } from "./chart.js"

const PHASE_LABEL = { IDLE: "Idle", TARE: "Tare", FAST: "Fast feed", DRIBBLE: "Dribble", SETTLE: "Settle", VERIFY: "Verify", MIX_RAMP: "Mix ramp",
  MIX_HOLD: "Mixing", MIX_COAST: "Coast", WAIT_TRANSFER: "Wait: tank", DRAIN: "Draining", EMPTY: "Empty" }
const RUNGS = {
  TARE: "--[ s0_Tare ]--[ WT_n < 0.3 kg ]--( s1_Fast )--",
  FAST: "--[ s1_Fast ]--[ WT_n < SP_n - Band ]--( XV_n01 )--",
  DRIBBLE: "--[ s2_Drib ]--[ WT_n < SP_n - Preact ]--( XV_n02 )--",
  SETTLE: "--[ s3_Settle ]--[ TON 6 s ]--( s4_Verify )--",
  VERIFY: "--[ s4_Verify ]--[ |WT_n - SP_n| <= Tol ]--( s5_Mix )--   else ( Preact += k*Err )",
  MIX_RAMP: "--[ s5_Mix ]--[ XV_n01..03 Closed ]--( M_n Run )--", MIX_HOLD: "--[ s5_Mix ]--[ TON 90 s ]--[ Shock < 3.2 g ]--( s6_Coast )--",
  MIX_COAST: "--[ s6_Coast ]--[ M_n ZeroSpeed ]--( s7_Wait )--", WAIT_TRANSFER: "--[ s7_Wait ]--[ TK200_Level < 72% ]--[ /TK200_HiHi ]--( XV_n03 )--",
  DRAIN: "--[ s8_Drain ]--[ WT_n > Heel ]--( P201 Run )--", EMPTY: "--[ s9_Empty ]--[ XV_n03 Closed ]--( MOV 0 Step )--( Batch N+1 )--", IDLE: "--[ Idle ]--",
}
/** Alarm age in process time: `12 s`, `3 min`, `2 h`. */
export const fmtAge = (s) => { s = Math.max(0, s); return s < 60 ? `${Math.floor(s)} s` : s < 3600 ? `${Math.floor(s / 60)} min` : `${Math.floor(s / 3600)} h` }
const LINE_ALARMS = new Set(["line.stopped", "tank.lolo"])
export const fmtKg = (x) => `${(Object.is(x, -0) || x > -0.05 ? Math.abs(x) : x).toFixed(1)} kg`
export function weightToVesselPct(kg) { const l = Math.max(0, Math.min(140, kg)) / 140; return Math.min(92, (0.55 * l + 0.45 * Math.sqrt(l)) * 90) }

export function drawGoldenPolar(canvas, unit, faulted) {
  if (!canvas || !canvas.getContext) return
  let ctx = null
  try { ctx = canvas.getContext("2d") } catch { ctx = null }
  if (!ctx) return
  const w = canvas.width, h = canvas.height, cx = w / 2, cy = h / 2
  ctx.clearRect(0, 0, w, h)
  ctx.fillStyle = "#0b1220"; ctx.beginPath(); ctx.arc(cx, cy, Math.min(cx, cy) - 4, 0, Math.PI * 2); ctx.fill()
  const pt = (deg, r) => { const a = (deg * Math.PI) / 180 - Math.PI / 2; return [cx + r * Math.cos(a), cy + r * Math.sin(a)] }
  ctx.strokeStyle = "rgba(34,211,238,0.25)"; ctx.lineWidth = 6; ctx.beginPath()
  for (let d = 0; d <= 360; d += 4) { const [x, y] = pt(d, 46 + 14 * Math.sin((d * Math.PI) / 180)); d === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y) }
  ctx.closePath(); ctx.stroke()
  const ang = ((PHASES.indexOf(unit.phase) + Math.min(1, unit.phaseT / 90)) / PHASES.length) * 360
  const bump = unit.shockPk > C.SHOCK_LIMIT_G ? 15 : 0
  ctx.strokeStyle = faulted ? "#f87171" : "#34d399"; ctx.lineWidth = 2; ctx.beginPath()
  for (let d = 0; d <= ang; d += 4) { const [x, y] = pt(d, 46 + 14 * Math.sin((d * Math.PI) / 180) + (d >= ang - 4 ? bump : 0)); d === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y) }
  ctx.stroke()
  const [mx, my] = pt(ang, 46 + 14 * Math.sin((ang * Math.PI) / 180) + bump)
  ctx.fillStyle = faulted ? "#fca5a5" : "#a7f3d0"; ctx.beginPath(); ctx.arc(mx, my, 5, 0, Math.PI * 2); ctx.fill()
}

/** The element standing for `id` under `root`: the root itself or a descendant, found by data-lens-of (a clone) or by id (the page). */
export function q(root, id) {
  if (root.getAttribute && root.getAttribute("data-lens-of") === id) return root
  return root.querySelector(`[data-lens-of="${id}"]`) || root.querySelector(`[id="${id}"]`)
}

export function createUI({ model, trend, audio, doc }) {
  // `R` is the root a render pass writes into: null = the page (by id); an element = a lens clone (by data-lens-of).
  let R = null, LK = null   // LK: an optional id -> element map for R, so a per-frame clone update does no DOM searching
  const $ = (id) => (R ? (LK ? LK.get(id) || null : q(R, id)) : doc.getElementById(id))
  // Tolerant helpers: an id absent from the host markup is skipped, never thrown on.
  const txt = (id, v) => { const e = $(id); if (e) e.textContent = v }
  const cname = (id, v) => { const e = $(id); if (e) e.className = v }
  const sty = (id, k, v) => { const e = $(id); if (e) e.style[k] = v }
  const tog = (id, c, on) => { const e = $(id); if (e) e.classList.toggle(c, on) }
  const on = (id, evt, fn) => { const e = $(id); if (e) e.addEventListener(evt, fn) }
  const all = (id, sel) => { const e = $(id); return e ? Array.from(e.querySelectorAll(sel)) : [] }
  let selected = null
  let paneHtml = ""
  let bannerKey = ""
  const cb = { speed: [], play: [], restart: [] }

  function stationCard(u) {
    return `
    <div class="station-card" id="card-${u.id}" data-unit="${u.id}" role="button" tabindex="0" aria-label="${u.id} ${u.name}, open operator pane">
      <div class="st-top"><span class="st-tag">${u.id}</span><span class="st-mode" id="mode-${u.id}">Prog</span></div>
      <div class="st-name-row"><div class="st-name" title="${u.name}">${u.name}</div><span class="wear-badge" id="wear-${u.id}" hidden>REBUILD DUE</span></div>
      <div class="vessel-wrap">
        <div class="vessel-sp-line" id="spline-${u.id}"><span class="vessel-sp-tag" id="sptag-${u.id}">SP ${u.sp.toFixed(1)} kg</span></div>
        <div class="vsym vsym-top" id="xv01-${u.id}" title="XV-${u.num}-1 fast valve"><span>F</span></div>
        <div class="vsym vsym-top vsym-drib" id="xv02-${u.id}" title="XV-${u.num}-2 dribble valve"><span>D</span></div>
        <div class="vessel-stream" id="stream-${u.id}"></div>
        <div class="mixer-assembly"><div class="mixer-motor-head" id="mtrhead-${u.id}"></div><div class="mixer-shaft"></div><div class="mixer-impeller" id="impeller-${u.id}"></div></div>
        <div class="vessel-liquid" id="liq-${u.id}"></div>
        <div class="vessel-overlay-metrics"><span id="wt-${u.id}">0.0 kg</span><span id="sp-${u.id}">/${u.sp.toFixed(1)}</span></div>
        <div class="vsym vsym-bottom" id="xv03-${u.id}" title="XV-${u.num}-3 outlet valve"><span>O</span></div>
      </div>
      <div class="device-row"><span class="dev-label">PHASE</span><span class="dev-state state-closed" id="phase-${u.id}">Idle</span></div>
      <div class="device-row"><span class="dev-label">FEED</span><span class="dev-state" id="flow-${u.id}">0.00 kg/s</span></div>
      <div class="device-row"><span class="dev-label">MIX M-${u.num}</span><span class="dev-state state-closed" id="mix-${u.id}">0 rpm</span></div>
      <div class="device-row"><span class="dev-label">SHOCK PK</span><span class="dev-state" id="vib-${u.id}">0.30 g</span></div>
    </div>`
  }

  function build() {
    const grid = $("stationsGrid"); if (grid) grid.innerHTML = model.units.map(stationCard).join("")
    // The dose is drawn in the station's own colour (CSS reads --liq); set once, the height is what moves.
    for (const u of model.units) { const liq = $(`liq-${u.id}`); if (liq) liq.style.setProperty("--liq", u.color) }
    for (const card of all("stationsGrid", ".station-card")) {
      card.addEventListener("click", () => openPane(card.dataset.unit))
      card.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPane(card.dataset.unit) } })
    }
    on("holdingTankCard", "click", () => openPane("TK-200"))
    on("holdingTankCard", "keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPane("TK-200") } })
    const upl = $("unitPhaseList"); if (upl) upl.innerHTML = model.units.map((u) => `<div class="unit-phase-row" id="uprow-${u.id}"><span style="color:${u.color}">${u.id}</span><span class="unit-phase-bar"><span id="upfill-${u.id}"></span></span><span id="uplabel-${u.id}">Idle</span></div>`).join("")
    const chips = $("windowChips"); if (chips) chips.innerHTML = WINDOWS.map((w) => `<button class="chip" data-window="${w}" type="button">${WINDOW_LABELS[w]}</button>`).join("") + `<button class="chip chip-live active" id="liveBtn" type="button">Live</button>`
    for (const b of all("windowChips", "[data-window]")) b.addEventListener("click", () => { trend.setWindow(Number(b.dataset.window)); audio.chime(600, 0.05) })
    on("liveBtn", "click", () => trend.setLive(true))
    trend.onView((v) => { for (const b of all("windowChips", "[data-window]")) b.classList.toggle("active", Number(b.dataset.window) === v.window); tog("liveBtn", "active", v.live) })
    on("recipeSelect", "change", (e) => { model.setRecipe(e.target.value); audio.chime(560, 0.06) })
    on("simSpeedSelect", "change", (e) => { for (const fn of cb.speed) fn(Number(e.target.value)) })
    on("playPauseBtn", "click", () => { for (const fn of cb.play) fn(); audio.chime(620, 0.06) })
    on("resetBatchBtn", "click", () => { for (const fn of cb.restart) fn(); audio.chime(740, 0.08) })
    for (const b of all("faultPanel", "[data-fault]")) b.addEventListener("click", () => { model.injectFault(b.dataset.fault); audio.chime(380, 0.1); renderFaults() })
    on("clearFaultsBtn", "click", () => { model.clearFaults(); audio.chime(700, 0.06); renderFaults() })
    on("rebuildShortcutBtn", "click", () => { model.rebuild("ST-104"); audio.chime(720, 0.08); render() })   // Controls-tab shortcut to the Operator pane action
    on("lineToggleBtn", "click", () => { model.setLineRunning(!model.tank.lineRunning); renderFaults() })
    on("opBody", "click", paneAction)
    on("alarmBanner", "click", (e) => {
      const b = e.target.closest ? e.target.closest("[data-ack]") : null; if (!b) return
      model.ackAlarm(b.dataset.ack); render()   // banner, equipment outlines and an open pane all steady at once
    })
    on("opClose", "click", closePane)
    on("opPane", "click", (e) => { if (e.target === $("opPane")) closePane() })
    doc.addEventListener("keydown", (e) => { if (e.key === "Escape") closePane() })
    renderFaults()
  }

  // `place` keeps the symbol's position classes: rewriting className without them dropped every symbol to the vessel floor.
  function vstate(el, v, place) {
    if (!el) return
    const s = v.state()
    el.className = `vsym ${place} vs-${s}${v.cmd ? " vs-cmd" : ""}`
  }
  const waitHeldOf = (u) => u.phase === "WAIT_TRANSFER" && (!model.tank.permissive || model.alarms.has("line.stopped"))
  /** One station's card (and its sequencer row): everything the per-unit DOM shows. */
  function renderUnit(u) {
    sty(`liq-${u.id}`, "height", `${weightToVesselPct(u.wt()).toFixed(1)}%`)
    txt(`wt-${u.id}`, fmtKg(u.wt())); txt(`sp-${u.id}`, `/${u.sp.toFixed(1)}`)
    sty(`spline-${u.id}`, "bottom", `${weightToVesselPct(u.sp).toFixed(1)}%`); txt(`sptag-${u.id}`, `SP ${u.sp.toFixed(1)} kg`)
    vstate($(`xv01-${u.id}`), u.xv01, "vsym-top"); vstate($(`xv02-${u.id}`), u.xv02, "vsym-top vsym-drib"); vstate($(`xv03-${u.id}`), u.xv03, "vsym-bottom")
    sty(`stream-${u.id}`, "opacity", u.flowIn > 0.01 ? "0.9" : "0"); sty(`stream-${u.id}`, "width", u.xv01.pos > 0.5 ? "5px" : "2px")
    tog(`impeller-${u.id}`, "spinning", u.mtr.rpm > 0); tog(`mtrhead-${u.id}`, "running", u.mtr.rpm > 0); tog(`mtrhead-${u.id}`, "fault", !u.conditions.shock_ok.ok)
    txt(`phase-${u.id}`, PHASE_LABEL[u.phase])
    // Waiting on a held tank is a hold, not a fault: amber, the same reading as the sequencer row.
    const waitHeld = waitHeldOf(u)
    cname(`phase-${u.id}`, "dev-state " + (u.phase === "FAST" ? "state-fast" : u.phase === "DRIBBLE" ? "state-dribble" : u.phase === "DRAIN" ? "state-open" : waitHeld ? "state-hold" : u.phase === "IDLE" ? "state-closed" : "state-done"))
    txt(`flow-${u.id}`, `${u.flowIn.toFixed(2)} kg/s`)
    txt(`mix-${u.id}`, `${Math.round(u.mtr.rpm)} rpm`); cname(`mix-${u.id}`, "dev-state " + (!u.conditions.shock_ok.ok ? "state-fault" : u.mtr.rpm > 0 ? "state-fast" : "state-closed"))
    txt(`vib-${u.id}`, `${u.shockPk.toFixed(2)} g`); sty(`vib-${u.id}`, "color", u.shockPk > C.SHOCK_LIMIT_G ? "var(--red)" : "var(--cyan)")
    txt(`mode-${u.id}`, u.cmdSrc + (u.hold ? " · HOLD" : ""))
    const wear = $(`wear-${u.id}`); if (wear) wear.hidden = !(u.xv02.rebuildDueBatches != null && u.xv02.rebuildDueBatches <= C.WEAR_DUE_BATCHES)
    tog(`card-${u.id}`, "faulted", isFaulted(u))
    annunciate(`card-${u.id}`, ownedBy(u.id))
    tog(`uprow-${u.id}`, "uprow-wait", waitHeld)
    txt(`uplabel-${u.id}`, waitHeld ? "Waiting: tank" : PHASE_LABEL[u.phase])
    sty(`upfill-${u.id}`, "width", `${(100 * PHASES.indexOf(u.phase)) / (PHASES.length - 1)}%`)
  }
  /** TK-200's card: level, mass, permissive / interlock pills, inlet pump, chain note, agitator, outline. */
  function renderTank() {
    const lineStopped = model.alarms.has("line.stopped")
    const tk = model.tank
    sty("tkLiquid", "height", `${tk.levelPct.toFixed(1)}%`); txt("tkLevelPct", `${tk.levelPct.toFixed(1)}%`); txt("tkMassKg", `${Math.round(tk.mass)} kg`)
    const waiting = model.units.filter((u) => u.phase === "WAIT_TRANSFER").length
    txt("tkPermissive", tk.permissive ? "PERMISSIVE OK (<72%)" : `PERMISSIVE HELD (≥72%) · ${waiting} waiting`); cname("tkPermissive", "pill " + (tk.permissive ? "pill-emerald" : "pill-amber"))
    const firstOut = model.units.filter((u) => u.conditions.tank_hihi.firstOut).map((u) => u.id)
    txt("tkInterlock", tk.interlock ? "HIGH-HIGH TRIP" + (firstOut.length ? ` · first out: ${firstOut.join(", ")}` : "") : "HIGH-HIGH CLEAR"); cname("tkInterlock", "pill " + (tk.interlock ? "pill-red" : "pill-emerald"))
    txt("tkInletState", tk.flowIn > 0.01 ? `PUMPING ${tk.flowIn.toFixed(1)} kg/s` : "OFF"); cname("tkInletState", "dev-state " + (tk.flowIn > 0.01 ? "state-open" : "state-closed"))
    txt("tkStateBadge", tk.interlock ? "TRIPPED" : tk.permissive ? "AVAILABLE" : "FULL")
    // The line-stop chain: while the line is stopped the tank fills, and the transfers will hold at the permissive.
    const chain = $("tkChainNote")
    if (chain) {
      chain.hidden = !lineStopped
      if (lineStopped) {
        const batchKg = model.units.reduce((s, u) => s + u.sp, 0) || 300
        const n = Math.max(0, Math.ceil(((C.TANK_PERMISSIVE_PCT / 100) * C.TANK_CAP - tk.mass) / batchKg))
        const msg = n > 0 ? `Permissive holds in ~${n} batch${n === 1 ? "" : "es"}` : "Permissive holding · transfers wait for the line"
        if (chain.textContent !== msg) chain.textContent = msg
      }
    }
    annunciate("holdingTankCard", ownedBy(null))
    const tkMixing = tk.mass > 0.05 * C.TANK_CAP
    tog("tkImpeller", "spinning", tkMixing); tog("tkAgitatorHead", "running", tkMixing)
  }
  /** Re-render one widget inside `root` (a lens clone) from the live model: a unit (object or id) or "TK-200". */
  function renderInto(root, key, lookup = null) {
    const prev = R, prevLK = LK; R = root; LK = lookup
    try {
      if (key === "TK-200") renderTank()
      else { const u = typeof key === "string" ? model.units.find((x) => x.id === key) : key; if (u) renderUnit(u) }
    } finally { R = prev; LK = prevLK }
  }
  function render() {
    for (const u of model.units) renderUnit(u)
    renderTank()
    const tk = model.tank
    annunciate("fillingLineCard", Array.from(model.alarms.values()).filter((a) => LINE_ALARMS.has(a.id)))
    renderAlarms()
    const lineState = tk.lineRunning ? (tk.flowOut > 0 ? "RUNNING" : "STARVED") : "STOPPED"
    txt("lineStatusBadge", lineState); cname("lineStatusBadge", "pill " + { RUNNING: "pill-emerald", STOPPED: "pill-amber", STARVED: "pill-red" }[lineState])
    // Animations follow the process: the jet and belt run only while the line draws (the belt pauses in place so jars
    // freeze rather than jump), the header flows while outlets pump, and the tank agitator needs liquid to turn.
    const dispensing = tk.flowOut > 0.01
    tog("nozzleJet", "dispensing", dispensing); tog("bottleBelt", "paused", !dispensing)
    tog("manifoldFlowFill", "active", tk.flowIn > 0.01)
    txt("dispenseFlowVal", `${tk.flowOut.toFixed(2)} kg/s`)
    txt("completedBatchesVal", String(model.batches.completed))
    txt("batchCounterPill", `BATCH #${model.batches.current}`)
    txt("processTimeLabel", `T${/^[+-]/.test(fmtClock(model.t)) ? "" : "+"}${fmtClock(model.t)}`)
    const focus = model.units.find((u) => u.id === selected) || model.units[0]
    txt("ladderRungBox", RUNGS[focus.phase])
    drawGoldenPolar($("goldenPolarCanvas"), focus, isFaulted(focus))
    const pill = $("goldenScorePill")
    if (pill) {
      const bad = Object.values(focus.conditions).some((c) => c.ok === false)
      pill.textContent = bad ? "RESIDUAL: 2.45 σ" : "RESIDUAL: 0.08 σ"; pill.className = "pill " + (bad ? "pill-amber" : "pill-emerald")
    }
    if (faultKeyNow() !== faultKey) renderFaults()
    const pane = $("opPane"); if (pane && !pane.hidden) renderPane()
  }

  let faultKey = ""
  function renderFaults() {
    faultKey = faultKeyNow()
    for (const b of all("faultPanel", "[data-fault]")) b.classList.toggle("active", model.faults.has(b.dataset.fault))
    txt("lineToggleBtn", model.tank.lineRunning ? "⏹ Stop filling line" : "▶ Start filling line")
  }

  // ---- Alarm annunciation: severity outline on the owning equipment, pulsing until every one of its alarms is acked.
  function ownedBy(unitId) { const out = []; for (const a of model.alarms.values()) if (a.unit === unitId) out.push(a); return out }
  function annunciate(id, list) {
    const crit = list.some((a) => a.severity === "alarm")
    tog(id, "alarm-critical", crit); tog(id, "alarm-warning", !crit && list.length > 0); tog(id, "alarm-pulse", list.some((a) => !a.acked))
  }
  const ackCell = (a, attrs) => a.acked ? `<span class="alarm-acked">ACKED</span>` : `<button class="btn alarm-ack" ${attrs} type="button" aria-label="Acknowledge: ${a.id}">ACK</button>`
  /** Banner + header count. Rows are rebuilt only when the set/order/ack state changes; text and age update in place (a click is never eaten). */
  function renderAlarms() {
    const list = Array.from(model.alarms.values()).sort((a, b) => b.raisedAt - a.raisedAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    const n = list.length, crit = list.some((a) => a.severity === "alarm")
    txt("alarmCountPill", n ? `${n} ALARM${n === 1 ? "" : "S"}` : "NO ALARMS")
    cname("alarmCountPill", "pill " + (!n ? "pill-emerald" : crit ? "pill-red" : "pill-amber"))
    const banner = $("alarmBanner"); if (!banner) return
    banner.hidden = n === 0
    const shown = list.slice(0, 2), more = n - shown.length
    const key = shown.map((a) => `${a.id}:${a.severity}:${a.acked ? 1 : 0}`).join("|") + `#${more}`
    if (key !== bannerKey) {
      bannerKey = key
      banner.innerHTML = shown.map((a) => `<div class="alarm-row sev-${a.severity}${a.acked ? "" : " alarm-pulse"}" data-alarm="${a.id}">` +
        `<span class="alarm-dot" aria-hidden="true"></span><span class="alarm-text"></span><span class="alarm-age"></span>${ackCell(a, `data-ack="${a.id}"`)}</div>`).join("") +
        (more > 0 ? `<div class="alarm-more">+${more} more</div>` : "")
    }
    for (const row of banner.querySelectorAll("[data-alarm]")) {
      const a = model.alarms.get(row.dataset.alarm); if (!a) continue
      const t = row.querySelector(".alarm-text"), g = row.querySelector(".alarm-age"), age = fmtAge(model.t - a.raisedAt)
      if (t.textContent !== a.text) { t.textContent = a.text; t.title = a.text }   // the title carries a text the row truncates
      if (g.textContent !== age) g.textContent = age
    }
  }
  function alarmSection(owned) {
    return `<div class="op-alarms"><h4>Active alarms</h4>` + (owned.length
      ? `<ul>${owned.map((a) => `<li class="alarm-row sev-${a.severity}${a.acked ? "" : " alarm-pulse"}"><span class="alarm-dot" aria-hidden="true"></span><span class="alarm-text">${F(`al:${a.id}`)}</span>${ackCell(a, `data-act="ack" data-id="${a.id}"`)}</li>`).join("")}</ul>`
      : `<p class="op-alarms-none">No active alarms</p>`) + `</div>`
  }
  function alarmFields(owned) { for (const a of owned) setField(`al:${a.id}`, a.text) }

  function faultKeyNow() { return Array.from(model.faults).sort().join(",") + "|" + model.tank.lineRunning }
  function openPane(id) { selected = id; const p = $("opPane"); if (p) p.hidden = false; audio.chime(600, 0.06); renderPane() }
  function closePane() { const p = $("opPane"); if (p) p.hidden = true }
  function isFaulted(u) { return !u.conditions.shock_ok.ok || !u.conditions.tare_ok.ok || (u.phase === "WAIT_TRANSFER" && model.tank.interlock) }
  // The pane's structure (buttons, labels, list skeleton) is rebuilt only when its key changes; live values are
  // written in place through data-field spans, so a button node is never replaced between mousedown and mouseup.
  function setField(name, v) { const body = $("opBody"); const e = body && body.querySelector(`[data-field="${name}"]`); if (e && e.textContent !== v) e.textContent = v }
  function structure(html) { if (html === paneHtml) return; paneHtml = html; $("opBody").innerHTML = html }
  // One delegated listener on the stable #opBody.
  function paneAction(e) {
    const b = e.target.closest ? e.target.closest("[data-act]") : null; if (!b) return
    if (b.dataset.act === "ack") { model.ackAlarm(b.dataset.id); render(); return }
    if (selected === "TK-200") { if (b.dataset.act === "line") { model.setLineRunning(!model.tank.lineRunning); renderFaults() } }
    else {
      const u = model.units.find((x) => x.id === selected); if (!u) return
      if (b.dataset.act === "hold") { if (u.hold) model.resumeUnit(u.id); else model.holdUnit(u.id) }
      else if (b.dataset.act === "rebuild") { model.rebuild(u.id); audio.chime(720, 0.08) }
    }
    renderPane()
  }
  const F = (name, init = "") => `<span data-field="${name}">${init}</span>`
  function renderPane() {
    if (!$("opTitle") || !$("opBody")) return
    if (selected === "TK-200") {
      const tk = model.tank
      txt("opTitle", "TK-200 · Holding tank")
      const owned = ownedBy(null)
      structure(alarmSection(owned) + `<div class="fp-grid">
        <div class="fp-stat"><div class="fp-stat-label">LEVEL</div><div class="fp-stat-val">${F("level")}</div></div>
        <div class="fp-stat"><div class="fp-stat-label">INFLOW / DISPENSE</div><div class="fp-stat-val">${F("flows")}</div></div>
        <div class="fp-stat"><div class="fp-stat-label">TRANSFER PERMISSIVE</div><div class="fp-stat-val">${F("perm")}</div></div>
        <div class="fp-stat"><div class="fp-stat-label">HIGH-HIGH INTERLOCK</div><div class="fp-stat-val">${F("hihi")}</div></div></div>
        <div class="op-actions"><button class="btn" data-act="line" type="button">${F("lineBtn")}</button></div>`)
      setField("level", `${tk.levelPct.toFixed(1)} % · ${Math.round(tk.mass)} kg`)
      setField("flows", `${tk.flowIn.toFixed(2)} / ${tk.flowOut.toFixed(2)} kg/s`)
      setField("perm", tk.permissive ? "OK (<72 %)" : "HELD (≥72 %)")
      setField("hihi", tk.interlock ? "TRIPPED (≥90 %)" : "CLEAR")
      setField("lineBtn", tk.lineRunning ? "Stop filling line" : "Start filling line")
      alarmFields(owned)
      return
    }
    const u = model.units.find((x) => x.id === selected); if (!u) return
    txt("opTitle", `${u.id} · ${u.name} — Operator`)
    const v2 = u.xv02, due = v2.rebuildDueBatches != null
    const key = `${u.id}|${u.hold}|${due}|${Object.values(u.conditions).map((c) => (c.ok ? 1 : 0) + (c.firstOut ? "f" : "")).join("")}`
    const owned = ownedBy(u.id)
    structure(`<!--${key}-->${alarmSection(owned)}
      <div class="op-head"><span>Command source: ${F("src")}</span>
        <span><button class="btn" data-act="hold" type="button">${u.hold ? "Resume (Prog)" : "Hold (Oper)"}</button></span></div>
      <div class="fp-grid">
        <div class="fp-stat"><div class="fp-stat-label">WEIGHT / SETPOINT</div><div class="fp-stat-val">${F("wt")}</div></div>
        <div class="fp-stat"><div class="fp-stat-label">PHASE</div><div class="fp-stat-val">${F("phase")}</div></div>
        <div class="fp-stat"><div class="fp-stat-label">PREACT / DRIBBLE BAND</div><div class="fp-stat-val">${F("preact")}</div></div>
        <div class="fp-stat"><div class="fp-stat-label">AGITATOR</div><div class="fp-stat-val">${F("agit")}</div></div>
      </div>
      <h4>Devices</h4>
      <ul class="op-devices">
        <li><span>XV-${u.num}-1 fast</span>${F("xv1s")}${F("xv1d")}</li>
        <li><span>XV-${u.num}-2 dribble</span>${F("xv2s")}${F("xv2d")}${due ? ` <span>rebuild due in ${F("due")} batches</span>` : ""}
          <button class="btn" data-act="rebuild" type="button">Rebuild</button></li>
        <li><span>XV-${u.num}-3 outlet</span>${F("xv3s")}${F("xv3d")}</li>
        <li><span>M-${u.num} agitator</span>${F("mtrs")}${F("mtrd")}</li>
      </ul>
      <h4>Conditions</h4>
      <ul class="op-conds">${Object.entries(u.conditions).map(([n, c]) => `<li class="cond ${c.ok ? "ok" : "bad"}${c.firstOut ? " first-out" : ""}"><span>${c.kind}</span><span>${c.label}</span><span>${c.ok ? "OK" : c.kind === "interlock" ? "TRIPPED" : "HELD"}${c.firstOut ? " · first out" : ""}</span></li>`).join("")}</ul>`)
    setField("src", u.cmdSrc + (u.hold ? " · HOLD" : ""))
    setField("wt", `${u.wt().toFixed(1)} / ${u.sp.toFixed(1)} kg`)
    setField("phase", `${PHASE_LABEL[u.phase]} · ${u.phaseT.toFixed(0)} s`)
    setField("preact", `${u.preact.toFixed(2)} / ${u.dribbleBand.toFixed(1)} kg`)
    setField("agit", `${Math.round(u.mtr.rpm)} / ${u.rpmTarget} rpm · ${u.shockPk.toFixed(2)} g`)
    setField("xv1s", u.xv01.state()); setField("xv1d", `${u.xv01.strokes} strokes · ${u.xv01.runH.toFixed(1)} h`)
    setField("xv2s", v2.state()); setField("xv2d", `${v2.strokes} strokes · close ${v2.tClose.toFixed(2)} s (new ${v2.tCloseNew.toFixed(2)} s)`)
    if (due) setField("due", String(v2.rebuildDueBatches))
    setField("xv3s", u.xv03.state()); setField("xv3d", `${u.xv03.strokes} strokes`)
    setField("mtrs", u.mtr.rpm > 0 ? "running" : "stopped"); setField("mtrd", `${u.mtr.runH.toFixed(1)} h`)
    alarmFields(owned)
  }

  // The spotlight (tour-dim / tour-highlight) belongs to lens.js; the UI only renders.
  return { build, render, renderInto, openPane, closePane, onSpeed: (fn) => cb.speed.push(fn), onPlayPause: (fn) => cb.play.push(fn), onRestart: (fn) => cb.restart.push(fn) }
}
